import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { SaleEPubDialogComponent, SaleEPubDialogData } from '@/components/sale-epub-dialog/sale-epub-dialog.component';
import {
  SaleUpdateBuyersDialogComponent,
  SaleUpdateBuyersDialogData
} from '@/components/sale-update-buyers-dialog/sale-update-buyers-dialog.component';
import {
  SaleUpdateDialogComponent,
  SaleUpdateDialogData
} from '@/components/sale-update-dialog/sale-update-dialog.component';
import {
  SaleUpdatePlayersDialogComponent,
  SaleUpdatePlayersDialogData
} from '@/components/sale-update-players-dialog/sale-update-players-dialog.component';
import {
  SaleUpdateStatusDialogComponent,
  SaleUpdateStatusDialogData
} from '@/components/sale-update-status-dialog/sale-update-status-dialog.component';
import { TimelineEvent } from '@/components/timeline/timeline.component';
import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { CurrencyPipe, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertySale, RealProperty, SalesManagementService, SellingStatus } from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TimelineComponent } from '../../../components/timeline/timeline.component';

@Component({
  selector: 'app-property-sale',
  templateUrl: './property-sale.component.html',
  styleUrls: ['./property-sale.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatCardModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    TimelineComponent,
    CurrencyPipe,
    TranslateModule
  ]
})
export class PropertySaleComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;

  sale: PropertySale;
  events: TimelineEvent[];
  canManageSale = false;

  realtorSellerName: string;
  realtorBuyerName: string;
  realtorUniqueName: string;
  notarySellerName: string;
  notaryBuyerName: string;
  notaryUniqueName: string;
  buyerName: string;
  coBuyerName: string;

  sub = new Subscription();
  deleteSub = new Subscription();

  constructor(
    private salesService: SalesManagementService,
    private userHelper: UserHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.canManageSale = this.userHelper.canManagePropertySale(this.prop);
    this.loadData();
  }

  loadData() {
    this.events = [];
    this.realtorUniqueName = null;
    this.notaryUniqueName = null;
    this.sub = this.salesService.getSale(this.prop.onGoingSelling.uid).subscribe((s) => {
      this.sale = s;
      this.realtorSellerName = s.sellerRealtor ? UserHelper.userSummmaryDisplayName(s.sellerRealtor) : null;
      this.realtorBuyerName = s.buyerRealtor ? UserHelper.userSummmaryDisplayName(s.buyerRealtor) : null;
      if (!s.sellerRealtor || !s.buyerRealtor || s.sellerRealtor === s.buyerRealtor)
        this.realtorUniqueName = this.realtorSellerName ?? this.realtorBuyerName;

      this.notarySellerName = s.sellerNotary ? UserHelper.userSummmaryDisplayName(s.sellerNotary) : null;
      this.notaryBuyerName = s.buyerNotary ? UserHelper.userSummmaryDisplayName(s.buyerNotary) : null;
      if (!s.sellerNotary || !s.buyerNotary || s.sellerNotary === s.buyerNotary)
        this.notaryUniqueName = this.notarySellerName ?? this.notaryBuyerName;

      this.buyerName = s.buyer ? UserHelper.userSummmaryDisplayName(s.buyer) : null;
      this.coBuyerName = s.coBuyer ? UserHelper.userSummmaryDisplayName(s.coBuyer) : null;

      this.events.push(this.createEvent(this.sale.dateOnSale, 'Mise en vente', 'real_estate_agent', true));
      if (this.sale.dateAgreement)
        this.events.push(
          this.createEvent(
            this.sale.dateAgreement,
            'Signature du compromis',
            'handshake',
            this.isStageValidated(SellingStatus.UNDER_AGREEMENT)
          )
        );
      if (this.sale.dateBankLoanApplication)
        this.events.push(
          this.createEvent(
            this.sale.dateBankLoanApplication,
            'Demande de prêt',
            'account_balance',
            this.isStageValidated(SellingStatus.BANK_LOAN_ACCEPTED)
          )
        );
      if (this.sale.dateBankLoanObtaining)
        this.events.push(
          this.createEvent(
            this.sale.dateBankLoanObtaining,
            'Obtention du prêt',
            'account_balance',
            this.isStageValidated(SellingStatus.BANK_LOAN_ACCEPTED)
          )
        );
      if (this.sale.dateSigning)
        this.events.push(
          this.createEvent(
            this.sale.dateSigning,
            'Signature de la vente',
            'fact_check',
            SellingStatus.SOLD === this.sale.status
          )
        );

      this.events.reverse();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  createEvent(date: string, details: string, icon: string, validated: boolean): TimelineEvent {
    const tIcons = [];
    if (validated) {
      tIcons.push({ matIcon: 'check_circle', tooltip: 'Étape validée' });
    }
    return {
      eventId: this.sale.uid,
      date: date,
      title: '',
      details: details,
      matIcon: icon,
      leftIcons: tIcons,
      canUpdate: false,
      canDelete: false
    };
  }

  isStageValidated(targetStatus: SellingStatus): boolean {
    return Object.keys(SellingStatus).indexOf(this.sale.status) >= Object.keys(SellingStatus).indexOf(targetStatus);
  }

  editSaleDates() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: SaleUpdateDialogData = { prop: this.prop, edition: true };
      const dialogRef = this.dialog.open(SaleUpdateDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) this.loadData();
      });
    }
  }

  editSaleStatus() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: SaleUpdateStatusDialogData = { sale: this.sale };
      const dialogRef = this.dialog.open(SaleUpdateStatusDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.SALE);
          // if status SOLD or CANCELED, redirect to property
          if ([SellingStatus.SOLD, SellingStatus.CANCELED].includes(result)) {
            this.router.navigate(['../'], { relativeTo: this.route });
          } else {
            this.loadData();
          }
        }
      });
    }
  }

  editSalePlayers() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: SaleUpdatePlayersDialogData = { sale: this.sale };
      const dialogRef = this.dialog.open(SaleUpdatePlayersDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }
  }

  addSaleBuyers() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: SaleUpdateBuyersDialogData = { sale: this.sale };
      const dialogRef = this.dialog.open(SaleUpdateBuyersDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }
  }

  confirmDeleteBuyers() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression des acquéreurs de cette vente ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteBuyers();
        }
      });
    }
  }

  doDeleteBuyers() {
    this.deleteSub = this.salesService.deleteSaleBuyers(this.sale.uid).subscribe(() => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }

  sendGenerateEPub() {
    // prevents to open multiple dialog
    const data: SaleEPubDialogData = { sale: this.sale };
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(SaleEPubDialogComponent, {
        data
      });
    }
  }
}
